import React, { useEffect, useState } from "react";
import { Link, withRouter } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { Auth } from "aws-amplify";
import * as Sentry from "@sentry/browser";

import styles from "./Sidebar.module.scss";

import { ReactComponent as BackIcon } from "../../assets/images/back-icon.svg";
import { ReactComponent as BillingIcon } from "../../assets/images/navbar/dollar-sign.svg";
import { ReactComponent as DashboardIcon } from "../../assets/images/navbar/dashboard.svg";
import { ReactComponent as FAQIcon } from "../../assets/images/navbar/faq.svg";
import { ReactComponent as LogoutIcon } from "../../assets/images/navbar/logout.svg";
import { ReactComponent as NotificationIcon } from "../../assets/images/navbar/notification.svg";
import { ReactComponent as PaymentIcon } from "../../assets/images/navbar/earnings.svg";
import { ReactComponent as ProfileIcon } from "../../assets/images/navbar/profile.svg";
import { ReactComponent as SettingsIcon } from "../../assets/images/navbar/settings.svg";

// eslint-disable-next-line jsx-a11y/anchor-has-content
const HrefLink = (props) => <a {...props} />;

const hrefWrapper = async (e, f) => {
  e.preventDefault();
  await f();
};

export const signOut = async () => {
  try {
    await Auth.signOut();
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setLevel("warning");
      Sentry.captureMessage(error);
    });
  }
};

const stages = {
  DEFAULT: "default",
  SETTINGS: "settings",
};

const components = {
  HASH: "hash",
  HREF: "href",
  LINK: "link",
};

const getComponent = (type) => {
  switch (type) {
    case components.HASH:
      return HashLink;
    case components.LINK:
      return Link;
    default:
      return HrefLink;
  }
};

const isActive = (pathNames = [], currentPathName, currentHash = "") =>
  pathNames.includes(`${currentPathName}${currentHash}`)
    ? `${styles.Item} ${styles.Active}`
    : styles.Item;

const Sidebar = ({ history }) => {
  const [stage, setStage] = useState(stages.DEFAULT);

  const headers = {
    [stages.DEFAULT]: {},
    [stages.SETTINGS]: {
      onClick: () => setStage(stages.DEFAULT),
      title: "Settings",
    },
  };

  const links = {
    [stages.DEFAULT]: [
      {
        icon: DashboardIcon,
        props: {
          to: "/",
        },
        svgStyle: styles.fill,
        text: "Dashboard",
        type: components.LINK,
        urls: ["/"],
      },
      {
        icon: BillingIcon,
        props: {
          to: "/#billing",
        },
        svgStyle: styles.stroke,
        text: "Billing",
        type: components.HASH,
        urls: ["/#billing"],
      },
      {
        icon: PaymentIcon,
        props: {
          to: "/payment-info",
        },
        svgStyle: styles.fill,
        text: "Payment Info",
        type: components.LINK,
        urls: ["/payment-info"],
      },
      {
        icon: SettingsIcon,
        props: {
          href: "/profile",
          onClick: (e) => hrefWrapper(e, () => setStage(stages.SETTINGS)),
        },
        svgStyle: styles.stroke,
        text: "Settings",
        type: components.HREF,
        urls: ["/profile/settings/account", "/profile/edit", "/profile/settings/notifications"],
      },
      {
        icon: FAQIcon,
        props: {
          href: "mailto:client@getmagicnow.com?subject=Support%20Dedicated%20Assistant",
          rel: "noreferrer",
          target: "_blank",
        },
        svgStyle: styles.stroke,
        text: "Support / FAQ",
        type: components.HREF,
        urls: ["/faq"],
      },
      {
        icon: LogoutIcon,
        props: {
          href: "/logout",
          onClick: (e) => hrefWrapper(e, signOut),
        },
        svgStyle: styles.stroke,
        text: "Logout",
        type: components.HREF,
        urls: ["/logout"],
      },
    ],
    [stages.SETTINGS]: [
      {
        icon: DashboardIcon,
        props: {
          to: "/profile/settings/account",
        },
        svgStyle: styles.fill,
        text: "Account Settings",
        type: components.LINK,
        urls: ["/profile/settings/account"],
      },
      {
        icon: ProfileIcon,
        props: {
          to: "/profile/edit",
        },
        svgStyle: styles.fill,
        text: "Profile",
        type: components.LINK,
        urls: ["/profile/edit"],
      },
      {
        icon: NotificationIcon,
        props: {
          to: "/profile/settings/notifications",
        },
        svgStyle: styles.fill,
        text: "Notifications",
        type: components.LINK,
        urls: ["/profile/settings/notifications"],
      },
    ],
  };

  useEffect(() => {
    if (
      ["/profile/settings/account", "/profile/edit", "/profile/settings/notifications"].includes(
        history.location.pathname
      )
    ) {
      setStage(stages.SETTINGS);
    }
  }, [history.location.pathname]);

  const header = headers[stage];
  const headerRow = header.title ? (
    <div
      className={`d-flex align-items-center ${styles.Header}`}
      data-testid={`Header-${header.title}`}
    >
      <BackIcon className="mr-3" data-testid="BackButton" role="button" onClick={header.onClick} />
      <h4>{header.title}</h4>
    </div>
  ) : null;

  const linkRows = links[stage].map((l, i) => {
    const Component = getComponent(l.type);
    const Icon = l.icon;

    return (
      <Component {...l.props} data-testid={l.text} key={i}>
        <li className={isActive(l.urls, history.location.pathname)}>
          <Icon className={`mr-2 ${l.svgStyle}`} />
          <span className={styles.ItemText}>{l.text}</span>
        </li>
      </Component>
    );
  });

  return (
    <div className={styles.Sidebar} data-testid="Sidebar">
      {headerRow}
      <ul className={styles.List}>{linkRows}</ul>
    </div>
  );
};

export default withRouter(Sidebar);
